/**
 * packUnit — single source of truth for "pack" vs "box" labelling.
 *
 * Drops carry a `pack_unit` field on the drop document ("pack" or "box").
 * Legacy drops without the field default to "pack" so nothing changes
 * for them until admin opens the drop and re-saves it with the new
 * dropdown selected.
 *
 * Usage:
 *   const u = getPackUnit(drop);
 *   <span>{u.totalLabel}</span>        → "Total Packs" or "Total Boxes"
 *   <span>{u.remainingLabel}</span>    → "Packs Remaining" or "Boxes Remaining"
 *   <span>{n} {u.plural(n)}</span>     → "5 packs" / "1 box"
 *   <span>{u.availableSentence(n)}</span> → "5 of 50 packs available"
 */

export const PACK_UNIT_OPTIONS = [
  { value: "pack", label: "Packs" },
  { value: "box", label: "Boxes" },
];

const UNITS = {
  pack: { singular: "Pack", plural: "Packs", lowerSingular: "pack", lowerPlural: "packs" },
  box:  { singular: "Box",  plural: "Boxes", lowerSingular: "box",  lowerPlural: "boxes" },
};

export function getPackUnit(drop) {
  const raw = (drop && (drop.pack_unit || drop.unit_label)) || "pack";
  const key = String(raw).toLowerCase() === "box" ? "box" : "pack";
  const u = UNITS[key];
  return {
    key,
    singular: u.singular,
    plural: u.plural,
    totalLabel: `Total ${u.plural}`,
    remainingLabel: `${u.plural} Remaining`,
    perPackLabel: `Per ${u.singular}`,
    plural_for: (n) => (Number(n) === 1 ? u.lowerSingular : u.lowerPlural),
    availableSentence: (remaining, total) => {
      const r = Number(remaining ?? 0);
      const t = Number(total ?? 0);
      const word = r === 1 ? u.lowerSingular : u.lowerPlural;
      return `${r} of ${t} ${word} available`;
    },
  };
}

/**
 * Hobby-aware series/drop label used on card overlays and badges.
 * - Hobby BOX drops → "Box Drop #N"
 * - Hobby PACK drops → "Pack Drop #N"
 * - Non-hobby → "Series N"
 * Pass the whole drop object; falls back to series_number=1 if missing.
 */
export function getHobbySeriesLabel(drop) {
  const n = drop?.series_number || 1;
  if (!drop?.is_hobby_drop) return `Series ${n}`;
  const u = getPackUnit(drop);
  return `${u.singular} Drop #${n}`;
}

export default getPackUnit;
